import { Navigate } from 'react-router-dom';
import { usePlatformModules } from '@/hooks/use-modules';
import { usePermissions } from '@/hooks/use-permissions';
import { useAuth } from '@/contexts/auth-context';
import { getModuleByKey } from '@/lib/modules';

interface ModuleRouteProps {
  moduleKey: string | string[];
  children: React.ReactNode;
}

export function ModuleRoute({ moduleKey, children }: ModuleRouteProps) {
  const { isModuleEnabled, isLoading } = usePlatformModules();
  const { isLoading: isAuthLoading } = useAuth();
  const { hasModuleAccess } = usePermissions();

  if (isLoading || isAuthLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
          <p className="text-sm text-gray-500">Loading module...</p>
        </div>
      </div>
    );
  }

  const keys = Array.isArray(moduleKey) ? moduleKey : [moduleKey];
  const enabledKeys = keys.filter((key) => isModuleEnabled(key));

  if (enabledKeys.length === 0) {
    const primaryKey = keys[0];
    const mod = getModuleByKey(primaryKey);
    const moduleName = mod?.name ?? primaryKey;
    return <Navigate to={`/module-not-enabled?module=${encodeURIComponent(moduleName)}`} replace />;
  }

  if (!enabledKeys.some((key) => hasModuleAccess(key))) {
    const primaryKey = enabledKeys[0];
    const mod = getModuleByKey(primaryKey);
    const moduleName = mod?.name ?? primaryKey;
    return (
      <Navigate
        to={`/module-not-enabled?module=${encodeURIComponent(moduleName)}&reason=no_access`}
        replace
      />
    );
  }

  return <>{children}</>;
}
